import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { SubscriptionTier, isFieldAvailable, getAvailableFields } from '../config/subscriptionTiers';
import { UpgradeModal } from '../components/common/UpgradeModal';
import { api } from '../utils/api';

interface SubscriptionStatusResponse {
  tier: SubscriptionTier;
  status: 'active' | 'cancelled' | 'expired';
  endDate: string;
}

interface SubscriptionContextType {
  tier: SubscriptionTier;
  isFieldEnabled: (fieldName: string) => boolean;
  availableFields: string[];
  showUpgradeModal: (feature?: string, requiredTier?: SubscriptionTier) => void;
  openPortal: () => Promise<void>;
  status: 'active' | 'cancelled' | 'expired';
  expiryDate?: Date;
  checkSubscriptionStatus: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [tier, setTier] = useState<SubscriptionTier>('FREE');
  const [status, setStatus] = useState<'active' | 'cancelled' | 'expired'>('active');
  const [expiryDate, setExpiryDate] = useState<Date>();
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState<string>();
  const [requiredTier, setRequiredTier] = useState<SubscriptionTier>();
  const [lastCheck, setLastCheck] = useState<number>(0);
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache

  const checkSubscriptionStatus = async () => {
    if (!user) return;

    // Check cache first
    const now = Date.now();
    if (now - lastCheck < CACHE_DURATION) {
      return;
    }

    try {
      const response = await api.get<SubscriptionStatusResponse>('/subscription/status');
      setLastCheck(now);
      
      const { tier: newTier, status: newStatus, endDate } = response.data;

      // If subscription has changed, update the tier
      if (newTier !== tier) {
        setTier(newTier);
      }

      setStatus(newStatus);
      setExpiryDate(new Date(endDate));
    } catch (error) {
      console.error('Error checking subscription status:', error);
      // Default to FREE tier on error
      setTier('FREE');
      setStatus('active');
    }
  };

  useEffect(() => {
    if (user) {
      checkSubscriptionStatus();
    } else {
      setTier('FREE');
      setStatus('active');
    }
  }, [user]); // Only depend on user

  // Check subscription status periodically
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(checkSubscriptionStatus, 60 * 60 * 1000); // Check every hour
    return () => clearInterval(interval);
  }, [user, checkSubscriptionStatus]); // Add proper dependencies

  const isFieldEnabled = (fieldName: string) => {
    return isFieldAvailable(fieldName, tier);
  };

  // Don't show upgrade modal while auth is loading
  const showUpgradeModal = (feature?: string, tier?: SubscriptionTier) => {
    if (!authLoading) {
      setUpgradeFeature(feature);
      setRequiredTier(tier);
      setIsUpgradeModalOpen(true);
    }
  };

  const openPortal = async () => {
    try {
      const response = await api.post<{ url: string }>('/subscription/create-portal-session', {});
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Error opening portal:', error);
    }
  };

  const value: SubscriptionContextType = {
    tier,
    isFieldEnabled,
    availableFields: getAvailableFields(tier),
    showUpgradeModal,
    openPortal,
    status,
    expiryDate,
    checkSubscriptionStatus,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
      <UpgradeModal
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
        currentTier={tier}
        requiredTier={requiredTier}
        feature={upgradeFeature}
      />
    </SubscriptionContext.Provider>
  );
};

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }

  return context;
}
